import type {
  HeadersFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { json } from '@remix-run/node'
import { getBlogHomePage } from '@helpers/getBlogHomePage'
import { BlogHome } from '@pages/BlogHome'
import {
  getBlogHomeContent,
  type BlogCategoryPicked,
} from '@helpers/getBlogHomeContent'
import { arrayIncludes } from '@helpers/arrayIncludes'
import { APP_LOCALES } from '@constants/i18n'
import { config } from '@infrastructure/config'
import { getCountryLanguageFromLocale } from '@helpers/getCountryLanguageFromLocale'
import { getCanonicalUrl } from '@helpers/getCanonicalUrl'
import { generatePageMeta } from '@helpers/generatePageMeta'
import { APP_FEATURES } from '@constants/features'
import { getRouteHeaders } from '@helpers/getRouteHeaders'
import { get404Response } from '@helpers/get404Response'
import { getCacheControlHeader } from '@helpers/getCacheControlHeader'

export type BlogHomeLoaderData = UnwrapLoaderData<typeof loader>

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
  const { locale } = params
  if (!locale || !arrayIncludes(APP_LOCALES, locale)) return []

  const { country, language } = getCountryLanguageFromLocale(locale)

  const { blogHomePageData, websiteUrl } = data as UnwrapLoaderData<
    typeof loader
  >
  const { linkedSeo } = blogHomePageData

  const slug = `${APP_FEATURES.BlogHome.prefix[language]}`

  const canonicalUrl = getCanonicalUrl({
    slug,
    country,
    language,
    baseUrl: websiteUrl,
  })

  const pageMeta = generatePageMeta({
    ...linkedSeo,
    ogUrl: canonicalUrl,
    // hardcoding this as there is no shouldIndex in the contentful model and this should always be true
    shouldIndex: true,
  })

  const canonical = {
    tagName: 'link',
    rel: 'canonical',
    href: canonicalUrl,
  }

  const metaDescription = {
    name: 'description',
    content: linkedSeo?.ogDescription ?? linkedSeo?.seoDescription,
  }

  return [canonical, ...pageMeta, metaDescription]
}

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const {
    contentCollection: categories,
    popularArticlesCollection,
    popularSectionPosition,
    allCategories,
    latestArticles,
    pageVariations,
    linkedSeo,
  } = (await getBlogHomePage(params, request)) || {}

  let contentCollection = [] as any[]

  if (categories) {
    contentCollection = await getBlogHomeContent(
      params,
      request,
      categories as BlogCategoryPicked[]
    )
  }

  if (!contentCollection) {
    throw get404Response(request)
  }

  const cacheControl = getCacheControlHeader({ request })

  return json(
    {
      blogHomePageData: {
        contentCollection,
        popularArticlesCollection,
        popularSectionPosition,
        allCategories,
        latestArticles,
        pageVariations,
        linkedSeo,
      },
      websiteUrl: config.websiteUrl,
    },
    {
      headers: {
        ...cacheControl,
      },
    }
  )
}

export const headers: HeadersFunction = getRouteHeaders

export default function Index() {
  const loaderData = useLoaderData() as BlogHomeLoaderData

  return <BlogHome loaderData={loaderData} />
}
